// TableContext.tsx
import { createContext, useContext } from 'react';
import { TableParams, TableColumn, TableHeaderAction, RowAction, HeaderBuiltins } from '../types';
import {
	useColumnsVisibilityOverride,
	useHandleRowAction,
	usePrioritizeOrder,
	useRowModalHandler,
	useTableMeta,
} from '../hooks';
import { useThModalHandler } from '../hooks/useTHModalHandler';
import { Filter } from '@/legacy/types';
import useTableRowsStore from '../hooks/useTableRowsStore';
import { usePageFiltersController } from '@/app/lib/page-filters/usePageFiltersController';

export interface TableContextType<TRow> {
	tableId: string;
	tableParams: TableParams;
	updateTableParams: (params: Partial<TableParams>) => void;
	handleTableChange: (...args: any[]) => void;
	handleSearch: (term: string, cols: string[]) => void;
	clearFilters: () => void;
	clearSearch: () => void;
	searchTerm: string;
	handleSearchInputChange: (value: string) => void;
	rowModalHandler: ReturnType<typeof useRowModalHandler>;
	thModalHandler: ReturnType<typeof useThModalHandler>;
	handleRowAction: ReturnType<typeof useHandleRowAction>;
	styledColumns: TableColumn<TRow>[];
	columns: TableColumn<TRow>[];
	updateFilters: (filter: Filter) => void;
	updateColumnsVisibility: (visibility: Record<string, boolean>) => void;
	customHeaderActions: TableHeaderAction[];
	setCustomHeaderActions: (actions: TableHeaderAction[]) => void;
	rowKey: string;
	rowActions: RowAction[];
	tableDataEndpoint?: string;
	forceNextDbFetch: () => void;
	useCacheRef: React.RefObject<boolean>;
	headerBuiltins: HeaderBuiltins;
	setHeaderBuiltins: (builtins: Partial<HeaderBuiltins>) => void;
	resetHeaderBuiltins: () => void;
	prioritizeLogic?: {
		isPrioritizing: boolean;
		enterPrioritize: () => void;
		exitPrioritize: () => void;
		togglePrioritize: () => void;
	} & ReturnType<typeof usePrioritizeOrder>;
	visibilityOverride?: ReturnType<typeof useColumnsVisibilityOverride>;
	tableMetaHandler: ReturnType<typeof useTableMeta>;
	tableRowsStore: ReturnType<typeof useTableRowsStore>;
	mergeGlobalFilter: boolean;
	pageFiltersController: ReturnType<typeof usePageFiltersController>;
}

export function createTableContext<TRow>() {
	return createContext<TableContextType<TRow> | null>(null);
}

export function createUseTableContext<TRow>(context: React.Context<TableContextType<TRow> | null>) {
	return function useTypedTableContext() {
		const ctx = useContext(context);
		if (!ctx) {
			throw new Error('useTableContext must be used within a TableProvider');
		}
		return ctx;
	};
}
